<script>
import range from 'lodash/range';
import { getYear, isAfter, parseISO } from 'date-fns';
import allProvinces from 'provinces';
import filter from 'lodash/filter';
import { mapState } from 'vuex';
import {hasEditPermission , hasViewPermission,currentRoleLevelName, trialActiveForClients} from '@/mixins/roleLevelPermissions';
import subscriptionHelpers from '@/mixins/subscriptionHelpers';
import { BIconLockFill } from 'bootstrap-vue';
import { validationMixin } from "vuelidate";
import { required } from "vuelidate/lib/validators";

export default {
	props: ['agreementData'],
	mixins:[validationMixin, subscriptionHelpers],
	data() {
		const year = getYear(new Date());

		return {
			agreementYearOptions: range(2022, year + 2),
			isViewMode : !hasEditPermission('section-basics'),
			currentRoleLevelName: currentRoleLevelName,
			showLearnButton: ['client-level-1', 'client-level-2'].includes(currentRoleLevelName),
			showUpgradeModal: false,
			restrictedSection: null
		};
	},
	methods: {
		validateState() {
			const { $dirty, $error } = this.$v.agreementData.agreement.updatedProvinceOfAgreement
			return $dirty ? !$error : null;
		},
		triggerValidation(){
			this.$v.agreementData.$touch()
			this.$refs.partynameinput1.triggerValidation()
			this.$refs.partynameinput2.triggerValidation()
			this.$refs.agreementYear.triggerValidation()
		},
		isSelected(option) {
			return this.agreementData.agreement.sections.includes(option.value);
		},
		toggleCheckbox(option) {
			const value = option.value;

			// If section is disabled (restricted by plan), show upgrade modal
			if (option.disabled && this.isUserSeparatingParty) {
				this.restrictedSection = option.label;
				this.showUpgradeModal = true;
				return;
			}

			// Normal toggle behavior
			if (this.isSelected(option)) {
				this.agreementData.agreement.sections = this.agreementData.agreement.sections.filter(item => item !== value);
			} else {
				this.agreementData.agreement.sections.push(value);
			}
		},
		showLearner() {
			window.open("https://learn.divii.ca/en/articles/11106489-matter-details", "_blank");
		},
		hideUpgradeModal() {
			this.showUpgradeModal = false;
			this.restrictedSection = null;
		}
	},
	computed:{
		...mapState(['user','currentRoleLevel']),
		Provinces(){
			const provinces = filter(allProvinces, { country: 'CA' });
			let filteredProvince = provinces.filter((province)=>province.name !== "Quebec" && province.name !== this.user?.locationOfPractice);
			// let filteredProvince = provinces.filter((province)=>province.name === "British Columbia"); /* Released when necessary */
			filteredProvince  = filteredProvince.map(obj => ({ ...obj, value: obj.name , text: obj.name }))
			if(this.user?.locationOfPractice){
				return [{name: this.user.locationOfPractice, value: this.user.locationOfPractice , text: this.user.locationOfPractice}, ...filteredProvince]
			}
			else{
				return filteredProvince
			}
		},
		propertyOptions(){
			let property=[
					'Family Residence',
					'Additional Real Property',
					'Business',
					'Corporation',
					'Pensions',
					'Canada Pension Plan',
					'RRSPs',
					'RESPs',
					'Financial Accounts',
					'Vehicles',
					'Other Property'
				]
			
			return property
			
		},
		sectionOptions() {
			const allSections = [
				{ label: 'Parenting', value: 'Parenting', disabled: false },
				{ label: 'Child Support', value: 'Child Support', disabled: false },
				{ label: 'Spousal Support', value: 'Spousal Support', disabled: false },
				{ label: 'Property', value: 'Property', disabled: false }
			];

			// Only apply restrictions for party users
			if (!this.isUserSeparatingParty) {
				return allSections;
			}

			// For party users, check if they have any payments
			const payments = this.$store.state.payments;
			const hasPayments = payments && payments.length > 0;

			if (!hasPayments) {
				// No payments - check if user is on a free trial
				const { trialEnd } = this.$store.state.userProfile;
				const isOnFreeTrial = trialEnd && isAfter(parseISO(trialEnd), new Date());
				if (isOnFreeTrial) {
					// On free trial - allow all sections
					return allSections;
				}
				// No payments and no trial - disable all sections
				return allSections.map(section => ({ ...section, disabled: true }));
			}

			// Get active plan type (from payments for party users)
			const activePlan = this.$store.getters.activePaymentForSeparatingParty;

			// If no active plan, disable all sections
			if (!activePlan) {
				return allSections.map(section => ({ ...section, disabled: true }));
			}

			// Get allowed sections for this plan
			const allowedSections = this.allowedSectionsForCurrentPlan;

			// Mark sections as disabled if not in allowed list
			return allSections.map(section => ({
				...section,
				disabled: !allowedSections.includes(section.value)
			}));
		},
		clientUserAgreement(){
			return trialActiveForClients() || currentRoleLevelName === 'client-sharing-user';
		},
		isUserSeparatingParty(){
			return this.currentRoleLevel?.roleAssociated === 'party'
		},
		dynamicQuestions(){
			if(this.isUserSeparatingParty){
				return {
					party1: 'Your Full Legal Name',
					party2: "Your Spouse's Legal Name"
				}
			}
			else{
				return {
					party1: 'Party 1',
					party2: "Party 2"
				}
			}
		},
	},
	mounted(){
		if(!this.agreementData.agreement.updatedProvinceOfAgreement){
			this.$set(this.agreementData.agreement, 'updatedProvinceOfAgreement', this.agreementData.provinceOfAgreement || null);
			// this.agreementData.agreement.updatedProvinceOfAgreement = this.agreementData.provinceOfAgreement
		}
		// if(!this.agreementData.agreement.updatedProvinceOfAgreement){
		// 	this.agreementData.agreement.updatedProvinceOfAgreement = this.Provinces[0].name;
		// }
	},
	validations:{
		agreementData:{
			partner1:{
				name:{
					first:{
						required
					},
					last:{
						required
					}
				}
			},
			partner2:{
				name:{
					first:{
						required
					},
					last:{
						required
					}
				}
			},
			agreement:{
				updatedProvinceOfAgreement:{
					required
				},
				year:{
					required
				}
			}
		}
	}
};
</script>

<template>
	<b-card>
		<div slot="header" class="section-heading">
			<h4>Matter Details</h4>
			<a href="#" onclick="Intercom('showArticle', 11106489)" v-if="this.showLearnButton">Learn &nbsp;<b-icon-book/></a>
		</div>

		<h4>Parties</h4>

		<party-name-input-group
			v-model="agreementData.partner1"
			:label="dynamicQuestions.party1"
			partner-id="partner1"
			class="mb-3"
			:testid="'matter-p1'"
			:disabled="isViewMode"
			ref="partynameinput1"
		/>

		<party-name-input-group
			v-model="agreementData.partner2"
			:label="dynamicQuestions.party2"
			partner-id="partner2"
			class="mb-3"
			:testid="'matter-p2'"
			:disabled="isViewMode"
			ref="partynameinput2"
		/>

		<h4>Agreement</h4>

		<!-- <b-form-group
				id="input-province-of-agreement"
				input-cols="8"
				label="Province of agreement"
				label-for="provinceOfAgreement"
			>
				<b-form-select
					id="provinceOfAgreement"
					v-model="agreementData.provinceOfAgreement"
					size="sm"
					:options="Provinces"
					text-field="name"
					value-field="name"
					data-testid='province-of-agreement'
					class="col-lg-8"
				/>
		</b-form-group> -->
		<p class="pt-2 field-required">Province of agreement</p>

		<b-row no-gutters>
			<b-col cols="4" class="col-12 col-sm-4">
				<b-form-select
					v-model="$v.agreementData.agreement.updatedProvinceOfAgreement.$model"
					:options="Provinces"
					:name="'Province of agreement'"
					data-testid="province-of-agreement"
					:disabled="isViewMode"
					:state="validateState()"
					:class="{ 'custom-disabled': isViewMode }"
				>
				</b-form-select>
				<div
					v-if="$v.agreementData.agreement.updatedProvinceOfAgreement.$error"
					class="invalid-feedback d-block col-12"
				>
					This is a required field.
				</div>
				<template #first>
					<b-form-select-option value="" disabled selected>
						Province
					</b-form-select-option>
				</template>
			</b-col>
			<b-icon-info-circle class="mt-2 ml-2"
				v-if="isUserSeparatingParty"
				v-b-tooltip="'Use the province where you reside.'">
			</b-icon-info-circle>
		</b-row>

		<!-- <radio-group
			v-model="agreementData.agreement.provinceOfAgreement"
			class="mb-3 my-3"
			input-cols="4"
			select
			reset-undefined
			label="Province of agreement"
			name="Province of agreement"
			:options="Provinces"
			:testid="'province-of-agreement'"
		/> -->
		<!-- {{ Provinces }}
{{ agreementYearOptions }} -->
		<radio-group
			v-model="agreementData.agreement.year"
			class="mb-3 my-3"
			input-cols="4"
			select
			label="Year of Agreement"
			name="agreement-year"
			:options="agreementYearOptions"
			:testid="'matter-year-of-agreement'"
			:disabled="isViewMode"
			:validation="true"
			ref="agreementYear"
			toolTipText="This is the year you will sign the Agreement. If you end up signing in the following year, you can come back here to change the year."
		/>

		<!-- <check-group
			v-model="agreementData.agreement.sections"
			class="mb-3"
			label="What sections would you like to include in the Agreement?"
			:options="sectionOptions"
			:testid="'matter-agreement-sections'"
			:id="'matter-agreement-sections'"
			:disabled="isViewMode"
		/> -->


		<b-form-group label="What sections would you like to include in the Agreement?" class="mb-3" :disabled="isViewMode">
			<template v-slot:label>
				<span>What sections would you like to include in the Agreement?</span>
				<b-icon-info-circle
					v-if="isUserSeparatingParty"
					v-b-tooltip="'You will almost certainly want to deal with Spousal Support and Property and if you have children you should also include Parenting and Child Support.'"
					class="ml-2">
				</b-icon-info-circle>
			</template>
			<div>
				<div
					v-for="(option,index) in sectionOptions"
					:key="option.value"
					@click="option.disabled && isUserSeparatingParty && !isViewMode ? toggleCheckbox(option) : null"
					:class="{ 'clickable-wrapper': option.disabled && isUserSeparatingParty && !isViewMode }"
				>
					<b-form-checkbox
						:id="'matter-agreement-sections_BV_option_'+index"
						:checked="agreementData.agreement.sections"
						@change="toggleCheckbox(option)"
						:value="option.value"
						:disabled="option.disabled || isViewMode"
						:class="{ 'restricted-checkbox': option.disabled && isUserSeparatingParty && !isViewMode }"
						data-testid="matter-agreement-sections"
					>
						{{ option.label }}
						<span v-if="option.disabled && isUserSeparatingParty && !isViewMode" class="upgrade-badge ml-2">
							<b-icon-lock-fill variant="secondary" font-scale="0.8"></b-icon-lock-fill>
							Upgrade
						</span>
					</b-form-checkbox>
				</div>
			</div>
		</b-form-group>

		<!-- Upgrade Modal -->
		<b-modal
			v-model="showUpgradeModal"
			title="Upgrade Your Plan"
			@hide="hideUpgradeModal"
			hide-footer
			centered
		>
			<p class="text-center mb-4">
				The <strong>{{ restrictedSection }}</strong> section is not included in your current plan.
				Upgrade to unlock this feature and create comprehensive separation agreements.
			</p>
			<div class="text-center">
				<b-button
					variant="primary"
					size="lg"
					@click="$router.push({ name: 'subscription' })"
				>
					Upgrade Plan
				</b-button>
			</div>
		</b-modal>

		<conditional-group
			class="mb-3"
			:condition="agreementData.agreement.sections.includes('Property') && !sectionOptions.find(option => option.value === 'Property').disabled"
			@reset="agreementData.agreement.propertyRecitals = []"
		>
			<check-group
				v-model="agreementData.agreement.propertyRecitals"
				label="What property should be detailed in the Agreement?"
				:options="propertyOptions"
				:reset-undefined="false"
				:testid="'matter-agreement-property-options'"
				:id="'matter-agreement-property-options'"
				:disabled="isViewMode"
			/>
		</conditional-group>

		<slot />
	</b-card>
</template>

<style scoped>
.clickable-wrapper {
	cursor: pointer;
}

.restricted-checkbox {
	opacity: 0.6;
}

.restricted-checkbox:hover {
	opacity: 0.8;
}

.upgrade-badge {
	color: #6c757d;
	font-size: 0.85em;
	font-weight: 500;
}
</style>
